/**
 * Signal helpers for the RSI-hero scanner UI.
 *
 * The strategy is a SEQUENTIAL GATE, evaluated in order:
 *      1. RSI momentum   →   2. ADX trend strength   →   3. EMA7 > EMA26
 *
 * These helpers derive a per-step pass / warn / fail status from the indicator
 * values the backend already returns, so the tile can show WHY a stock landed in
 * its bucket instead of a flat grid of numbers.
 *
 * Thresholds are intentionally centralised and tunable — adjust here to match the
 * private strategy without touching any component code.
 */
import { ScanResultDto } from '../types/api';

export type GateStatus = 'pass' | 'warn' | 'fail';

/**
 * Tunable cut-offs, aligned with the backend strategy
 * (Infrastructure/Strategy/IndicatorEngine.ComputeState):
 *   BUY  = EMA7 > EMA26  AND  RSI > 55  AND  DI+ > DI-
 *   HOLD = RSI > 35  AND (uptrend OR close > MA50)  AND  DI+ > DI-
 *   SELL = downtrend AND (RSI < 35 OR RSI slope < -3) AND DI- dominant AND ADX > 20
 * Caveat: the backend's second BUY condition is a DI+/DI- cross, not an ADX
 * level; ADX 20 here is the strategy's only real ADX cut-off (SELL confirmation),
 * and the warn floor below it is display-only.
 */
export const THRESHOLDS = {
  // RSI: BUY momentum > 55; 35–55 is HOLD territory; < 35 feeds SELL
  rsi: { pass: 55, warn: 35 },
  // ADX: 20 is the strategy's only cut-off; 15–20 "forming" band is display-only
  adx: { pass: 20, warn: 15 },
  // EMA cross counts as "warn" when the two EMAs are within this % of each other
  // (display-only — the backend uses a strict EMA7 > EMA26 comparison)
  emaWarnBandPct: 0.3,
  // RSI tone bands for colour (standard overbought / oversold, display-only)
  rsiOverbought: 70,
  rsiOversold: 30,
} as const;

export interface Indicators {
  rsi: number | null;
  adx: number | null;
}

/** Safely parse the backend `indicators` JSON string. */
export function parseIndicators(r: ScanResultDto): Indicators {
  if (!r.indicators) return { rsi: null, adx: null };
  try {
    const i = JSON.parse(r.indicators);
    return {
      rsi: typeof i.rsi === 'number' ? i.rsi : null,
      adx: typeof i.adx === 'number' ? i.adx : null,
    };
  } catch {
    return { rsi: null, adx: null };
  }
}

export function rsiStatus(rsi: number | null): GateStatus {
  if (rsi == null) return 'fail';
  if (rsi >= THRESHOLDS.rsi.pass) return 'pass';
  if (rsi >= THRESHOLDS.rsi.warn) return 'warn';
  return 'fail';
}

export function adxStatus(adx: number | null): GateStatus {
  if (adx == null) return 'fail';
  if (adx >= THRESHOLDS.adx.pass) return 'pass';
  if (adx >= THRESHOLDS.adx.warn) return 'warn';
  return 'fail';
}

export function emaStatus(emaFast: number | null, emaSlow: number | null): GateStatus {
  if (emaFast == null || emaSlow == null || emaSlow === 0) return 'fail';
  const spread = ((emaFast - emaSlow) / emaSlow) * 100;
  if (Math.abs(spread) <= THRESHOLDS.emaWarnBandPct) return 'warn';
  return spread > 0 ? 'pass' : 'fail';
}

/** The three gate steps, in evaluation order: [RSI, ADX, EMA7>EMA26]. */
export function gateOf(r: ScanResultDto): [GateStatus, GateStatus, GateStatus] {
  const { rsi, adx } = parseIndicators(r);
  return [rsiStatus(rsi), adxStatus(adx), emaStatus(r.emaFast, r.emaSlow)];
}

/** How many gate steps are aligned (passing). */
export function gatePassCount(r: ScanResultDto): number {
  return gateOf(r).filter(s => s === 'pass').length;
}

export type RsiTone = 'hot' | 'cold' | 'mid';
export function rsiTone(rsi: number | null): RsiTone {
  if (rsi == null) return 'mid';
  if (rsi >= THRESHOLDS.rsiOverbought) return 'hot';
  if (rsi <= THRESHOLDS.rsiOversold) return 'cold';
  return 'mid';
}

export function rsiStateLabel(rsi: number | null): string {
  if (rsi == null) return 'no data';
  if (rsi >= THRESHOLDS.rsiOverbought) return 'overbought';
  if (rsi <= THRESHOLDS.rsiOversold) return 'oversold';
  return 'neutral';
}

/** EMA7 vs EMA26 spread as a signed %. */
export function emaSpreadPct(r: ScanResultDto): number | null {
  if (r.emaFast == null || r.emaSlow == null || r.emaSlow === 0) return null;
  return ((r.emaFast - r.emaSlow) / r.emaSlow) * 100;
}

/** Price vs EMA7 as a signed %. */
export function vsEmaPct(r: ScanResultDto): number | null {
  if (r.emaFast == null || r.emaFast === 0) return null;
  return ((r.currentPrice - r.emaFast) / r.emaFast) * 100;
}

export const GATE_LABELS = ['RSI', 'ADX', 'EMA▲'] as const;
export const GATE_FULL = ['RSI momentum', 'ADX trend strength', 'EMA7 > EMA26'] as const;

export function fmtINR(n: number | null | undefined, prefix = '₹'): string {
  if (n == null) return '—';
  return `${prefix}${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}
